import React, { useRef } from 'react';
import { type Control, Controller, type FieldPath, type FieldValues } from 'react-hook-form';
import {
    type FieldErrorState,
    type FormItemChildren,
    emptyValueOf,
    renderFormItemChildren,
} from './formItemState';

interface FormItemProps {
    control: Control<FieldValues, unknown>;
    name: FieldPath<FieldValues>;
    children: FormItemChildren;
}

export const FormItem: React.FC<FormItemProps> = ({ control, name, children }) => {
    // A pristine field is still showing this form's default for it, which is the only place
    // the shape the form uses for "empty" is observable. Re-read while pristine rather than
    // once: `reset()` and async defaults change that shape long after the first render.
    const emptyValueRef = useRef<unknown>('');

    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState }) => {
                if (!fieldState.isDirty) {
                    emptyValueRef.current = emptyValueOf(field.value);
                }
                const state: FieldErrorState = {
                    error: !!fieldState.error,
                    message: fieldState.error?.message,
                    onFieldChange: field.onChange,
                    onFieldBlur: field.onBlur,
                    emptyValue: emptyValueRef.current,
                    value: field.value,
                };

                return (
                    <div data-field-name={field.name} data-has-error={state.error}>
                        {renderFormItemChildren(children, state)}
                    </div>
                );
            }}
        />
    );
};

export default FormItem;
